import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18nService } from '@app/i18n/services';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { LegalsService } from '@services/legals';
import { ProgressIndicatorService } from '@services/progress';
import { FormValidators, IFormContent } from '@tcp13equals2/z5-lib';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { DialogService } from '../../../../../../services/dialog';
import { FormEditorInputType } from '../../../../../../shared/components';
import { UpdateLegal } from './legal-app-update.models';

@Component({
	selector: 'app-app-legal-update',
	templateUrl: './legal-app-update.dialog.html'
})
export class LegalAppUpdateDialogComponent implements OnInit {
	private dialogRefClose: () => void;

	public formChanges: IFormContent = {};
	public formChangesBase: IFormContent = {};
	public hasChanges = false;
	public formEditorInputType = FormEditorInputType;
	public formInputs = this.formBuilder.group({
		name: ['', [FormValidators.required]],
		alias: [''],
		localisation: this.formBuilder.group({})
	});

	constructor(
		private legalsService: LegalsService,
		private i18nService: I18nService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		@Inject(MAT_DIALOG_DATA) public dialogData: UpdateLegal,
		private dialogRef: MatDialogRef<LegalAppUpdateDialogComponent>,
		private formBuilder: FormBuilder,
		private dialogService: DialogService
	) {
		// Store original close function and replace with unsaved version, remember to restore on use
		this.dialogRefClose = this.dialogService.interceptClose(this.dialogRef, this, this.closeDialog);
	}

	public ngOnInit(): void {
		this.formInputs.controls.name.setValue(this.dialogData.terms.displayName);
		this.formInputs.controls.alias.setValue(this.dialogData.terms?.alias ? this.dialogData.terms.alias : '');

		// Wait for form to stabilize
		setTimeout(() => {
			this.formChangesBase = { ...this.formInputs.value };
		});
	}

	public formChange(change: IFormContent): void {
		this.formChanges = change;
		this.hasChanges = Object.keys(this.formChanges).length > 0;
	}

	public onFormSubmit(): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get('Are you sure you want to save the changes?', 'app.prompt.generic.confirm-save-message'),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) this.updateLegal();
			}
		});
	}

	public closeDialog(): void {
		if (this.hasChanges) {
			this.feedbackService.confirm({
				title: FeedbackTitle.warning,
				message: this.i18nService.get(
					'You have unsaved changes that will be lost. Are you sure you want to continue?',
					'app.prompt.pending-changes-confirm-message'
				),
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) this.dialogRefClose();
				}
			});
		} else {
			this.dialogRefClose();
		}
	}

	private updateLegal(): void {
		this.progressIndicatorService.spinner(true);

		const serviceCalls = {};

		if (Object.keys(this.formChanges).filter((key) => key !== 'localisation').length > 0) {
			serviceCalls['update'] = this.legalsService
				.updateTerms(this.dialogData.terms.termsId, this.formChanges?.name, this.formChanges?.alias)
				.pipe(catchError((error) => of(error)));
		}

		if (this.formChanges?.localisation) {
			const payload = {
				displayNames: {}
			};

			for (const [locale, localisation] of Object.entries(this.formChanges.localisation)) {
				payload.displayNames[locale] = localisation['displayName'];
			}

			serviceCalls['lokalise'] = this.legalsService
				.localiseTerms(this.dialogData.terms.termsId, payload)
				.pipe(catchError((error) => of(error)));
		}

		forkJoin(serviceCalls)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe({
				next: (res) => {
					const errors: HttpErrorResponse[] = [];

					// Gather errors to display and reset formChangesBase to ensure we capture saved/unsaved changes
					for (const [key, value] of Object.entries(res)) {
						if (value instanceof HttpErrorResponse) {
							errors.push(value);
						} else {
							if (key === 'lokalise') {
								this.formChangesBase.localisation = {
									...this.formChangesBase.localisation,
									...this.formChanges.localisation
								};
							} else if (key === 'update') {
								this.formChangesBase = {
									...this.formChangesBase,
									...Object.values(value).filter((val) => val !== 'lokalisation')
								};
							}
						}
					}

					errors.forEach((err) => this.feedbackService.apiError(err));

					this.dialogData.callback(false);

					if (errors.length !== Object.keys(res).length) {
						this.feedbackService.notification({
							msg: this.i18nService.get('The document has been updated.', 'app.prompt.legal.term-updated'),
							config: { duration: 4000 }
						});
					}

					if (errors.length === 0) this.dialogRefClose();
				}
			});
	}
}
